const ACCOUNT_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getAccountIdQueryParam(): string | null {
  return new URLSearchParams(window.location.search).get('account_id');
}

function parseAccountId(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return null;
  }

  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

function parseAccountUuid(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  let segment: string;
  try {
    segment = decodeURIComponent(value);
  } catch {
    return null;
  }

  return ACCOUNT_UUID_RE.test(segment) ? segment.toLowerCase() : null;
}

export function parseAccountIdQueryParam(): number | null {
  return parseAccountId(getAccountIdQueryParam());
}

export function parseAccountUuidQueryParam(): string | null {
  return parseAccountUuid(getAccountIdQueryParam());
}

/**
 * Extract a leading digits-only account id from a path (`/{account_id}/…`).
 * Matches Brandbot account-scoped URLs such as `/12636/dashboard`.
 *
 * @returns The account id as an integer, or null when the first segment is
 *   missing or non-numeric.
 */
export function getAccountIdFromPath(path: string): number | null {
  if (typeof path !== 'string') {
    return null;
  }

  const match = path.match(/^\/(\d+)(?:\/|$)/);
  if (!match) {
    return null;
  }

  return parseAccountId(match[1]);
}

/**
 * Account id from the current browser URL. Prefers `/{account_id}` in the path,
 * then falls back to a digits-only `?account_id` query param.
 */
export function getAccountIdFromUrl(): number | null {
  const fromPath = getAccountIdFromPath(window.location.pathname);
  if (fromPath) {
    return fromPath;
  }

  return parseAccountIdQueryParam();
}

export function getAccountUuidFromPath(path: string): string | null {
  if (typeof path !== 'string') {
    return null;
  }

  const match = path.match(/^\/account\/([^/]+)(?:\/|$)/);
  if (!match) {
    return null;
  }

  return parseAccountUuid(match[1]);
}

export function getAccountUuidFromUrl(): string | null {
  const fromQuery = parseAccountUuidQueryParam();
  if (fromQuery) {
    return fromQuery;
  }

  return getAccountUuidFromPath(window.location.pathname);
}
